import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";

const LEVEL_BG = {
  critical: "bg-red-600 text-white",
  high: "bg-orange-500 text-white",
  medium: "bg-amber-400 text-slate-900",
  low: "bg-green-500 text-white",
};

const LIKELIHOOD_LABELS = ["Rare", "Unlikely", "Possible", "Likely", "Almost certain"];
const IMPACT_LABELS = ["Insignificant", "Minor", "Moderate", "Major", "Catastrophic"];

export default function RiskMatrixPage() {
  const navigate = useNavigate();
  const [matrix, setMatrix] = useState(null);
  const [residual, setResidual] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/risks/heatmap/matrix?residual=${residual}`)
      .then(({ data }) => setMatrix(data.matrix))
      .finally(() => setLoading(false));
  }, [residual]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Risk Heat Map</h1>
          <p className="text-sm text-muted-foreground mt-1">
            5×5 matrix. Each cell shows the count of risks at that Likelihood × Impact rating.
          </p>
        </div>
        <div className="inline-flex rounded-md border border-border overflow-hidden">
          <button
            data-testid="matrix-inherent"
            onClick={() => setResidual(false)}
            className={cn("px-4 h-10 text-sm font-medium", !residual ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted")}
          >
            Inherent
          </button>
          <button
            data-testid="matrix-residual"
            onClick={() => setResidual(true)}
            className={cn("px-4 h-10 text-sm font-medium", residual ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted")}
          >
            Residual
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>{residual ? "Residual" : "Inherent"} risk matrix</CardTitle>
            <CardDescription>Click any cell to view the risks in that quadrant.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading || !matrix ? (
              <div className="h-96 bg-muted animate-pulse rounded" />
            ) : (
              <div className="flex">
                {/* Y-axis (Impact) label */}
                <div className="flex flex-col items-center justify-center pr-3 -rotate-90 h-72 text-xs uppercase tracking-wider text-muted-foreground">
                  Impact →
                </div>
                <div className="flex-1">
                  <div className="grid grid-cols-6 gap-1">
                    {/* corner */}
                    <div />
                    {IMPACT_LABELS.map((_, li) => (
                      <div key={`likelihood-header-${li + 1}`} className="text-[10px] text-center text-muted-foreground pb-1">L{li + 1}</div>
                    ))}
                    {matrix.map((row, ri) => {
                      const impactLevel = 5 - ri; // top row = impact 5
                      return (
                        <React.Fragment key={`impact-row-${impactLevel}`}>
                          <div className="text-[10px] text-right pr-1 text-muted-foreground flex items-center justify-end">I{impactLevel}</div>
                          {row.map((cell) => (
                            <button
                              key={`cell-${cell.impact}-${cell.likelihood}`}
                              data-testid={`matrix-cell-${cell.impact}-${cell.likelihood}`}
                              onClick={() => setSelectedCell(cell)}
                              className={cn(
                                "aspect-square rounded-md flex flex-col items-center justify-center font-semibold transition-transform hover:scale-105 border border-transparent",
                                LEVEL_BG[cell.level],
                                selectedCell?.impact === cell.impact && selectedCell?.likelihood === cell.likelihood && "ring-2 ring-primary ring-offset-2 ring-offset-background"
                              )}
                              title={`I=${cell.impact} · L=${cell.likelihood} · score=${cell.score}`}
                            >
                              <span className="text-lg tabular-nums leading-none">{cell.count}</span>
                              <span className="text-[9px] opacity-80 mt-0.5">{cell.score}</span>
                            </button>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </div>
                  <div className="mt-2 grid grid-cols-6 gap-1">
                    <div />
                    {LIKELIHOOD_LABELS.map((l) => (
                      <div key={l} className="text-[10px] text-center text-muted-foreground truncate" title={l}>{l}</div>
                    ))}
                  </div>
                  <div className="text-center text-xs text-muted-foreground uppercase tracking-wider mt-2">Likelihood →</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Selected quadrant</CardTitle>
            <CardDescription>
              {selectedCell
                ? `Impact ${selectedCell.impact} × Likelihood ${selectedCell.likelihood} = ${selectedCell.score}`
                : "Click any cell in the matrix"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!selectedCell && <div className="text-sm text-muted-foreground">Nothing selected.</div>}
            {selectedCell && selectedCell.risks.length === 0 && (
              <div className="text-sm text-muted-foreground">No risks in this cell.</div>
            )}
            <ul className="space-y-2">
              {selectedCell && selectedCell.risks.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => navigate(`/risks/${r.id}`)}
                    className="w-full text-left px-3 py-2 rounded-md border border-border hover:bg-muted transition-colors"
                  >
                    <div className="text-sm font-medium truncate">{r.title}</div>
                    <div className="text-xs text-muted-foreground">{r.category} · {r.status}</div>
                  </button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Legend</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-3 text-xs">
          {["low", "medium", "high", "critical"].map((k) => (
            <div key={k} className="flex items-center gap-1.5">
              <span className={cn("w-3 h-3 rounded", LEVEL_BG[k])} />
              <span className="capitalize">{k}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
